/* eslint-disable react/jsx-wrap-multilines */

/* eslint-disable react/no-unused-prop-types */
import React from 'react';

import * as styles from './styles';
import RadioGroup from '@material-ui/core/RadioGroup';
import moment from 'moment';

import type { DateRange } from '@cobliteam/common-types';

import type { WebData } from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import {
  DialogWithHeader,
  MultiDatePicker,
  RadioButton,
  SingleDatePicker,
} from '@cobliteam/base-components';

import { Button } from '@cobliteam/ui/components';

import SaveAltRoundedIcon from '@mui/icons-material/SaveAltRounded';

export interface ExportAlertModalProps {
  incidentsReportWebData: WebData<void>;
  loading: boolean;
  downloadIncidentsReport: (period: DateRange) => void;
  onCancel: () => void;
}

export interface ExportAlertModalState {
  selectedDate: string;
  invalidSelectedRange: boolean;
  exportPeriod: DateRange;
}

export enum DateOptions {
  DAY = 'day',
  PERIOD = 'period',
}

const DEFAULT_SELECTED_PERIOD_DAYS = 7;
const LIMIT_PERIOD_SELECTION = 31;

interface ButtonGroupProps {
  loading: boolean;
  invalidSelectedRange: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  onCancel,
  loading,
  invalidSelectedRange,
  onSubmit,
}) => (
  <div className={styles.action}>
    <Button variant='text' onClick={onCancel} className='mr-10'>
      Cancelar
    </Button>

    <Button
      loading={loading}
      disabled={invalidSelectedRange}
      startIcon={<SaveAltRoundedIcon />}
      onClick={onSubmit}
    >
      Exportar
    </Button>
  </div>
);

export class ExportAlertModal extends React.Component<
  ExportAlertModalProps,
  ExportAlertModalState
> {
  constructor(props: ExportAlertModalProps) {
    super(props);
    this.state = {
      selectedDate: DateOptions.DAY,
      invalidSelectedRange: false,
      exportPeriod: {
        start: moment().startOf('day').valueOf(),
        end: moment().valueOf(),
      },
    };
  }

  public componentDidUpdate = (prevProps: ExportAlertModalProps) => {
    const { loading, onCancel } = this.props;
    if (prevProps.loading && !loading) {
      onCancel();
    }
  };

  private handleExport = () => {
    const { exportPeriod } = this.state;
    const { downloadIncidentsReport } = this.props;

    Analytics.track('Incidents Report Exported');
    downloadIncidentsReport(exportPeriod);
  };

  private handleRadioDateChange = (
    event: React.ChangeEvent<{}>,
    selectedDate: string
  ) => {
    if (selectedDate === DateOptions.DAY) {
      const exportPeriod = {
        start: moment().startOf('day').valueOf(),
        end: moment().valueOf(),
      };
      this.setState({
        invalidSelectedRange: false,
        selectedDate,
        exportPeriod,
      });
    } else if (selectedDate === DateOptions.PERIOD) {
      const exportPeriod = {
        start: moment()
          .subtract(DEFAULT_SELECTED_PERIOD_DAYS, 'days')
          .startOf('day')
          .valueOf(),
        end: moment().valueOf(),
      };
      this.setState({
        invalidSelectedRange: false,
        selectedDate,
        exportPeriod,
      });
    }
    Analytics.track('Export Alert Modal Interval Filter Type Changed', {
      option: selectedDate,
    });
  };

  private handleDateChange = (start: number, end: number) => {
    const daysRange = moment(end).diff(moment(start), 'days');
    Analytics.track('Export Alert Modal Interval Filter Value Changed', {
      start,
      end,
    });

    if (daysRange >= LIMIT_PERIOD_SELECTION) {
      this.setState({ invalidSelectedRange: true });
    } else {
      const exportPeriod = { start, end };
      this.setState({ exportPeriod, invalidSelectedRange: false });
    }
  };

  private handleDayChange = (beginDayInMillis: number) => {
    Analytics.track(
      'Export Alert Modal Interval Filter Default Value Changed',
      { begin: beginDayInMillis }
    );

    const exportPeriod = {
      start: beginDayInMillis,
      end: moment(beginDayInMillis).endOf('day').valueOf(),
    };

    this.setState({ exportPeriod });
  };

  public render = () => {
    const { onCancel, loading } = this.props;
    const { selectedDate, invalidSelectedRange, exportPeriod } = this.state;

    return (
      <DialogWithHeader
        open
        title='Exportar relatório de alertas'
        onClose={onCancel}
        actions={
          <ButtonGroup
            onCancel={onCancel}
            loading={loading}
            onSubmit={this.handleExport}
            invalidSelectedRange={invalidSelectedRange}
          />
        }
      >
        <div className={styles.contentContainer}>
          <h1 className={styles.info}>
            Selecione um dia único ou um período de até 31 dias:
          </h1>
          <RadioGroup
            value={selectedDate}
            onChange={this.handleRadioDateChange}
            className={styles.radioGroup}
          >
            <RadioButton value={DateOptions.DAY} label='Dia único' />
            <RadioButton value={DateOptions.PERIOD} label='Período' />
          </RadioGroup>
          <div className={styles.pickerWrapper}>
            {selectedDate === DateOptions.DAY && (
              <div className={styles.pickerDate}>
                <SingleDatePicker
                  handleDayChange={this.handleDayChange}
                  selectedDateInMillis={exportPeriod.start}
                  disabledDays={{ after: new Date() }}
                />
              </div>
            )}
            {selectedDate === DateOptions.PERIOD && (
              <div className={styles.pickerPeriod}>
                <MultiDatePicker
                  handleRangeChange={this.handleDateChange}
                  startDate={exportPeriod.start}
                  endDate={exportPeriod.end}
                  showWarningError={invalidSelectedRange}
                  showErrorMessage={false}
                  disabledDays={{ after: new Date() }}
                />
              </div>
            )}
          </div>
          <div className={styles.errorWrapper}>
            {selectedDate === DateOptions.PERIOD && invalidSelectedRange && (
              <h1 className={styles.errorInfo}>
                Selecione um período de até 31 dias
              </h1>
            )}
          </div>
        </div>
      </DialogWithHeader>
    );
  };
}
